import { LocationStrategy, PathLocationStrategy } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  provideZonelessChangeDetection
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { TranslateModule } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { RECAPTCHA_SETTINGS, RecaptchaFormsModule, RecaptchaModule, RecaptchaSettings } from 'ng-recaptcha-2';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { providePrimeNG } from 'primeng/config';
import { SelectModule } from 'primeng/select';
import SagaPurplePreset from 'src/theme/saga-purple-preset';

import { GlobalErrorHandler } from './_handlers';
import { authInterceptor, httpErrorInterceptor, loadingSpinnerInterceptor } from './_interceptors';
import { APP_ROUTES } from './app.routes';
import { LanguageService } from './_services/language.service';

const LIVE_HOST = 'www.fusion-platform.com';
const STAGING_HOST = 'd3txz191q445kh.cloudfront.net';

const CAPTCHA_STAGING = '6LfzCCcdAAAAAIDSawFrzYQSIsrMFLtWmBFON5gU';
const CAPTCHA_LIVE = '6Ldp-yYdAAAAAG-esDbYrKOGZRGdArPqQbMDFZxG';
const CAPTCHA_TEST = '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideRouter(APP_ROUTES),
    provideAnimations(),
    provideHttpClient(withInterceptors([authInterceptor, httpErrorInterceptor, loadingSpinnerInterceptor])),
    providePrimeNG({
      ripple: true,
      theme: {
        preset: SagaPurplePreset,
        options: {
          prefix: 'p',
          darkModeSelector: false,
          cssLayer: {
            name: 'primeng',
            order: 'theme, base, primeng, utilities'
          }
        }
      }
    }),
    importProvidersFrom(
      FormsModule,
      SelectModule,
      TranslateModule.forRoot({
        loader: provideTranslateHttpLoader({
          prefix: './assets/i18n/',
          suffix: '-i18n.json'
        }),
        fallbackLang: 'en'
      }),
      RecaptchaModule,
      RecaptchaFormsModule,
      NgIdleKeepaliveModule.forRoot()
    ),
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler
    },
    {
      provide: LocationStrategy,
      useClass: PathLocationStrategy
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (languageService: LanguageService) => () => languageService.initializeApp(),
      deps: [LanguageService],
      multi: true
    },
    {
      provide: RECAPTCHA_SETTINGS,
      useValue: {
        siteKey:
          window.location.host === LIVE_HOST
            ? CAPTCHA_LIVE
            : window.location.host === STAGING_HOST
              ? CAPTCHA_STAGING
              : CAPTCHA_TEST
      } as RecaptchaSettings
    },
    MessageService,
    ConfirmationService,
    DialogService
  ]
};
